import { useDrop } from "react-dnd";
import { defineMessages } from "react-intl";
import classNames from "classnames";
import { useSortableListInputContext } from ".";
import { SORTABLE_DRAGGABLE_LIST_SOURCE } from "./SortableListDraggableSource";
import SortableListDraggableSource from "./SortableListDraggableSource";
import { useFormatMessage } from "../../Intl/Context";

const SortableListDroppableZone = () => {
  const { addToSelectedItems, selectedItems, moveSelectedItems, maximumOptions } =
    useSortableListInputContext();

  const t = useFormatMessage();
  const placeholderLabel = t(intlMessages.placeholder);
  const placeholderDraggingLabel = t(intlMessages.placeholderDragging);
  const maxReachedLabel = t(intlMessages.maxReached, { max: maximumOptions });

  const [{ isOver, canDrop, willOverflow }, droppableRef] = useDrop(
    () => ({
      accept: SORTABLE_DRAGGABLE_LIST_SOURCE,
      drop: (item, monitor) => {
        if (!maximumOptions || selectedItems.length < maximumOptions) {
          addToSelectedItems(item, monitor);
        }
      },
      collect: monitor => {
        const draggedItem = monitor.getItem();
        const isOver = monitor.isOver();
        const canDrop = !maximumOptions || selectedItems.length < maximumOptions;
        const notAlreadySelected = draggedItem && !draggedItem.isSelected;

        const willOverflow =
          isOver &&
          notAlreadySelected &&
          maximumOptions &&
          selectedItems.length >= maximumOptions;

        return {
          isOver,
          canDrop,
          draggedItem,
          willOverflow
        };
      }
    }),
    [selectedItems, maximumOptions, addToSelectedItems]
  );

  const noSelectedItems = selectedItems.length === 0;

  const className = classNames("sortable-list-droppable-zone", "padding-x-xxs", {
    "has-new-item-over": isOver && canDrop,
    "has-max-reached": willOverflow,
    "d-flex": noSelectedItems,
    "align-items-center": noSelectedItems,
    "justify-content-center": noSelectedItems,
    "text-center": noSelectedItems
  });

  const placeholder =
    noSelectedItems && (isOver ? placeholderDraggingLabel : placeholderLabel);

  return (
    <div ref={droppableRef} className={className}>
      {placeholder && <p className="padding-xs text-muted font-italic">{placeholder}</p>}
      {selectedItems.map(option => (
        <SortableListDraggableSource
          key={option.id}
          option={option}
          addToSelectedItems={addToSelectedItems}
          selectedItems={selectedItems}
          moveSelectedItems={moveSelectedItems}
          showPosition
        />
      ))}
      {willOverflow && (
        <div className="text-danger font-italic small mt-2">{maxReachedLabel}</div>
      )}
    </div>
  );
};

export default SortableListDroppableZone;

const intlMessages = defineMessages({
  placeholder: {
    id: "inputs.sortableList.droppableZone.placeholder",
    defaultMessage: `
      Glissez et déposez dans cette zone les options depuis la colonne de gauche pour les
      prioriser.
    `
  },
  placeholderDragging: {
    id: "inputs.sortableList.droppableZone.placeholderDragging",
    defaultMessage: " Déposez ici l'option pour l'ajouter à la liste de priorisation. "
  },
  maxReached: {
    id: "inputs.sortableList.droppableZone.maxReached",
    defaultMessage: " Vous avez atteint la limite de {max} options sélectionnées. "
  }
});
